import { cloneDeep } from '../../../../../util/lodashlike';

const generateId = () => {
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

// FIN-13536 — BE contract: children1 is List[QueryChildren] (QueryGroup.scala:23,94), an
// ARRAY. RAQB's own serializer (Utils.getTree) emits arrays; the stored/wire shape is always
// an array too (measured: 3516 ARRAY / 0 OBJECT across scenario_typologies). Only the
// hand-rolled clone/merge helpers below ever produced an object keyed by child id, and the
// BE decoder rejects that with a 422. Assert defensively so a future regression fails loudly
// here instead of only surfacing as "Error Parsing Fraud Rules" on save.
const assertArrayChildren1 = (node) => {
  if (
    node &&
    node.children1 !== undefined &&
    node.children1 !== null &&
    !Array.isArray(node.children1)
  ) {
    throw new Error(
      'treeUtils: children1 must be an array (BE contract ' +
        `List[QueryChildren]); got ${typeof node.children1}`
    );
  }
  return node;
};

const regenerateIds = (node) => {
  if (!node || typeof node !== 'object') return node;
  const next = { ...node, id: generateId() };
  if (node.children1 && typeof node.children1 === 'object') {
    // children1 is an array — the BE contract shape (List[QueryChildren]), NOT a map keyed
    // by child id. Object.values reads either an array or a legacy/RAQB-round-tripped object
    // input; the output is always rebuilt as an array. See FIN-13536.
    next.children1 = Object.values(node.children1).map((child) =>
      regenerateIds(child)
    );
  }
  return assertArrayChildren1(next);
};

// Walk tree and re-key children1 maps so each map key equals the child's id.
// react-awesome-query-builder requires this invariant; historical data persisted
// before the regenerateIds fix may have key/id mismatches that crash the lib
// (see treeUtils.js:352 `items[childId].depth` undefined).
export const normalizeChildrenKeys = (node) => {
  if (!node || typeof node !== 'object') return node;
  const next = { ...node };
  if (node.children1 && typeof node.children1 === 'object') {
    const newChildren = {};
    Object.values(node.children1).forEach((child) => {
      if (!child) return;
      const fixedChild = normalizeChildrenKeys(child);
      newChildren[fixedChild.id] = fixedChild;
    });
    next.children1 = newChildren;
  }
  return next;
};

export const isEmptyTree = (tree) => {
  if (!tree) return true;
  // FIN-13537 — a stored `{}` (no id, no type) is not a usable group: it is the AFC
  // "function with no condition" shape (FIN-13382's hasUsableTree case). Without this guard
  // the check below falls through (undefined !== 'group') and reports "not empty", so the
  // merge branch runs and cloneTreeWithFreshIds({}) yields a type-less node the BE rejects
  // with "children1.type: Missing required field".
  if (!hasUsableTree(tree)) return true;
  if (tree.type !== 'group' && tree.type !== 'group_ext') return false;
  return !tree.children1 || Object.keys(tree.children1).length === 0;
};

export const cloneTreeWithFreshIds = (tree) => {
  if (!tree) return null;
  return regenerateIds(cloneDeep(tree));
};

export const mergeTreesAsAnd = (a, b) => {
  const aEmpty = isEmptyTree(a);
  const bEmpty = isEmptyTree(b);
  if (aEmpty && bEmpty) return null;
  if (aEmpty) return cloneTreeWithFreshIds(b);
  if (bEmpty) return cloneTreeWithFreshIds(a);

  const clonedA = cloneTreeWithFreshIds(a);
  const clonedB = cloneTreeWithFreshIds(b);
  return assertArrayChildren1({
    id: generateId(),
    type: 'group',
    properties: { conjunction: 'AND', not: false },
    // children1 is an array — BE contract (List[QueryChildren]). See FIN-13536.
    children1: [clonedA, clonedB]
  });
};

// FIN-13223 — Utils.checkTree drops any leaf whose field is missing from the picker config and
// reports nothing: the condition renders as a blank "Select field" with no reason given. A field
// goes missing when it was renamed (the v3 canonical/legacy split) or deliberately disabled
// (custom_data, txn_date_time_completion in #11396). Diffing the field ids either side of
// checkTree names what went missing. RAQB's own sanitizeTree returns the errors, but it is a
// different call with different defaults — removeIncompleteRules among them — and swapping to it
// would change what Save allows.
const collectFieldIds = (node, acc = new Set()) => {
  if (!node || typeof node !== 'object') return acc;
  const field = node.properties?.field;
  if (typeof field === 'string') acc.add(field);
  // children1 is an array in stored JSON and an object once RAQB has round-tripped it.
  Object.values(node.children1 || {}).forEach((child) =>
    collectFieldIds(child, acc)
  );
  return acc;
};

export const droppedFieldIds = (before, after) => {
  const kept = collectFieldIds(after);
  return [...collectFieldIds(before)].filter((id) => !kept.has(id));
};

// FIN-13382 — is this stored value something Utils.loadTree can actually load?
//
// RAQB throws "Can't load tree!" for an object carrying neither `id` nor `type`, and a stored
// `{}` is truthy, so the usual `value || <fresh group>` guard sails straight past it. AFC
// typologies save `{}` for a function with no condition, so this is the real persisted shape,
// not a defensive hypothetical. Absent/null values were always fine — they are falsy and take
// the fallback — but they are included here so callers need only one predicate.
export function hasUsableTree(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return value.id !== undefined || value.type !== undefined;
}
